"use client"

import { Form } from "@/components/form"
import { SubmitButton } from "@/components/submit-button"
import { Translate } from "@/components/translate"
import { Button } from "@/components/ui/button"
import { CheckboxField } from "@/components/ui/checkbox-field"
import { Loader } from "@/components/ui/loader"
import { arrayCheckbox } from "@/config/consts"
import { useConfigParams } from "@/hooks/use-config-params"
import {
  DEVICE_ID_COOKIE_NAME,
  DeviceInfo,
  useDeviceInfo,
} from "@/hooks/use-device-info"
import { useLoginError } from "@/hooks/use-login-error"
import { usePostLogin } from "@/hooks/use-post-login"
import { useRecaptcha } from "@/hooks/use-recaptcha"
import { useCustomSearchParams } from "@/hooks/use-search-params"
import { trackAnalyticsEvent } from "@/lib/analytics"
import { AuthType } from "@/lib/auth-type"
import { createRequiredField } from "@/lib/validations/required"
import { useOAuthLoginMutation } from "@/services/post-login-oauth"
import {
  TOAuthLoginV2Response,
  useOAuthLoginV2Mutation,
} from "@/services/post-login-oauth-v2"
import {
  BaseLoginPayload,
  TloginErrorResponse,
  TSsoLoginResponse,
  useSsoLoginV1Mutation,
} from "@/services/post-login-sso"
import {
  TSsoLoginV3Response,
  useSsoLoginV3Mutation,
} from "@/services/post-login-sso-v3"
import { useDeviceInfoStore } from "@/store/device-info-store"
import { usePersonalDataStore } from "@/store/personal-data-store"
import { DevTool } from "@hookform/devtools"
import { zodResolver } from "@hookform/resolvers/zod"
import { isErr, isOk, Result } from "@pacote/result"
import Cookies from "js-cookie"
import dynamic from "next/dynamic"
import { useController, useForm } from "react-hook-form"
import { z } from "zod"
import {
  LoginFormPasswordInput,
  PASSWORD_REQUIRED_ERROR,
} from "./login-form-password-input"
import {
  LoginFormUsernameInput,
  USERNAME_REQUIRED_ERROR,
} from "./login-form-username-input"
import { MFA_DIALOG_PARAM } from "./mfa-verification-dialog/mfa-verification-dialog.config"
import { recoveryPasswordParam } from "./password-recovery-dialog/password-recovery-dialog.config"
import { verifyContacts } from "./post-login-dialog/post-login-dialog.config"

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const LazyAccountBlockedModal = dynamic(
  () => import("./account-blocked-dialog"),
)

const LazyForeignAccountIncompleteDialog = dynamic(
  () => import("./foreign-account-incomplete-dialog"),
)
const LazyForeignAccountIncompleteDialogFooter = dynamic(
  () => import("./foreign-account-incomplete-dialog/components/footer.dialog"),
)

const LazyExpiringPasswordDialog = dynamic(
  () => import("./expiring-password-dialog"),
)
const LazyPostLoginDialog = dynamic(
  () => import("./post-login-dialog/post-login-dialog"),
)
// const LazyPostLoginWelcomeDialog = dynamic(
//   () => import("./post-login-dialog/post-login-welcome-dialog"),
// )
const LazyPasswordRecoveryDialog = dynamic(
  () => import("./password-recovery-dialog/password-recovery-dialog"),
)
const LazyMultiModalDialog = dynamic(() => import("./multimodal-dialog"))

const LazyMfaVerificationDialog = dynamic(
  () => import("./mfa-verification-dialog/mfa-verification-dialog"),
)

const FormSchema = z.object({
  usernameEtc: createRequiredField(USERNAME_REQUIRED_ERROR),
  password: createRequiredField(PASSWORD_REQUIRED_ERROR),
  rememberUsername: z.boolean(),
})

export type LoginFormValues = z.infer<typeof FormSchema>

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/

export function LoginForm() {
  /*--------------------hooks--------------------*/
  const [searchParams, setSearchParams] = useCustomSearchParams()
  const { channel, authType } = useConfigParams()
  const { triggerPostLoginAction } = usePostLogin()

  const oAuthLoginMutation = useOAuthLoginMutation()
  const oAuthLoginV2Mutation = useOAuthLoginV2Mutation()
  const ssoLoginV1Mutation = useSsoLoginV1Mutation()
  const ssoLoginV3Mutation = useSsoLoginV3Mutation()

  const { setPassword, setUsername } = usePersonalDataStore()

  const getRecaptchaToken = useRecaptcha("LOGIN")
  const getDeviceInfo = useDeviceInfo()

  const { handleLoginError } = useLoginError()
  /*--------------------variables config--------------------*/
  const isInformationDisplayDialogVisible =
    searchParams.get("informationDisplay") === "open"

  const showRememberUsernameField = arrayCheckbox.includes(channel)
  const canShowDialogs =
    ((!!oAuthLoginMutation.data && isOk(oAuthLoginMutation.data)) ||
      (!!oAuthLoginV2Mutation.data && isOk(oAuthLoginV2Mutation.data)) ||
      (!!ssoLoginV1Mutation.data && isOk(ssoLoginV1Mutation.data)) ||
      (!!ssoLoginV3Mutation.data && isOk(ssoLoginV3Mutation.data))) &&
    !isInformationDisplayDialogVisible
  /*--------------------helpers--------------------*/

  const setFormError = (message: string) => {
    form.setError("usernameEtc", {
      type: "custom",
      message: `Errors.emptyError`,
    })
    form.setError("password", {
      type: "custom",
      message,
    })
  }

  const fetchLoginResponse = async (
    username: string,
    password: string,
  ): Promise<
    Result<
      TSsoLoginResponse | TSsoLoginV3Response | TOAuthLoginV2Response,
      TloginErrorResponse
    >
  > => {
    const { token: recaptchaToken, platform } = await getRecaptchaToken()

    const baseLoginPayload: BaseLoginPayload = {
      username,
      password,
      channel,
      recaptchaToken,
    }

    if (window.APP_COUNTRY === "IT") {
      const deviceInfo = await getDeviceInfo()

      saveDeviceInfo(deviceInfo)

      const { deviceId, deviceName, appName, pushId } = deviceInfo

      switch (authType) {
        case AuthType.OAuth:
          return await oAuthLoginV2Mutation.trigger({
            ...baseLoginPayload,
            device: {
              deviceId,
              deviceName: appName,
              deviceDescription: deviceName,
              pushId,
            },
            channel,
          })
        case AuthType.Sso:
        default:
          return await ssoLoginV3Mutation.trigger({
            ...baseLoginPayload,
            device: { deviceId },
            platform,
          })
      }
    } else {
      switch (authType) {
        case AuthType.OAuth:
          return await oAuthLoginMutation.trigger({
            ...baseLoginPayload,
            channel,
          })
        case AuthType.Sso:
        default:
          return await ssoLoginV1Mutation.trigger({
            ...baseLoginPayload,
            platform,
          })
      }
    }
  }
  /*--------------------form config--------------------*/

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      usernameEtc: Cookies.get("username") ?? "",
      password: "",
      rememberUsername: false,
    },
  })

  const rememberUsernameField = useController({
    name: "rememberUsername",
    control: form.control,
  }).field

  /*--------------------submit handler--------------------*/
  const onSubmit = form.handleSubmit(async (data) => {
    trackAnalyticsEvent({
      event: "link_click",
      event_name: "nav_click",
      conversion_pagename: "login",
      position: "login",
      cta_label: "accedi",
    })

    setSearchParams((prevParams) => {
      prevParams.delete("errorBanner", "serviceError")
      return prevParams
    })

    if (!isEmail(data.usernameEtc)) {
      setUsername(data.usernameEtc)
      Cookies.set("username", data.usernameEtc, { expires: 30 })
    }
    setPassword(data.password)

    const response = await fetchLoginResponse(data.usernameEtc, data.password)

    if (isErr(response)) {
      handleLoginError(response, setFormError)
      return
    }

    const { postLoginAction, loginBlockedType } = response.value

    triggerPostLoginAction({
      postLoginActionType: postLoginAction.type,
      args: {
        response: response.value,
        loginBlockedType,
        showListModals: postLoginAction?.showListModals,
        setFormError,
        isTrustedDevice: isTrustedDevice(response.value),
      },
    })
  })

  /*--------------------event handlers --------------------*/
  const handlePasswordBlur = () => {
    const passwordValue = form.getValues("password")

    if (!passwordValue) {
      form.setError("password", {
        type: "custom",
        message: PASSWORD_REQUIRED_ERROR,
      })
    }
  }

  // when we change the value of a field following an ‘invalidCredentials’ error
  // we must reset the errors so that the error status is also removed from the username field
  const handlePasswordChange = () => {
    if (form.formState.errors.usernameEtc?.message === "Errors.emptyError") {
      form.trigger()
    }
  }
  const handleUsernameChange = () => {
    if (form.formState.errors.usernameEtc?.message === "Errors.emptyError") {
      form.trigger()
    }
  }

  return (
    <>
      <Form {...form}>
        {form.formState.isSubmitting && <Loader />}
        <form onSubmit={onSubmit} className="flex w-full flex-col gap-6">
          {/*--------------------input fields--------------------*/}
          <LoginFormUsernameInput
            name="usernameEtc"
            control={form.control}
            onChange={handleUsernameChange}
          />

          <LoginFormPasswordInput
            name="password"
            control={form.control}
            onBlur={handlePasswordBlur}
            onChange={handlePasswordChange}
          />

          <div>
            {/*--------------------remember username field--------------------*/}
            {showRememberUsernameField && (
              <div className="flex gap-2.5">
                <CheckboxField {...rememberUsernameField} />
                <label
                  htmlFor="rememberUsername"
                  className="cursor-p text-field-text text-[0.75rem] font-light"
                >
                  <Translate
                    i18nKey={"LoginPage.LoginForm.rememberUsernameFieldLabel"}
                  />
                </label>
              </div>
            )}
            <Button
              data-qa="login-form-forgot-password"
              onClick={() => {
                setSearchParams((prevParams) => {
                  prevParams.set(recoveryPasswordParam, "true")
                  return prevParams
                })
              }}
              variant="ghost"
              className="place-self-start pl-0 underline"
            >
              <Translate i18nKey={"LoginPage.forgotPassword"} />
            </Button>
          </div>
          {/*--------------------submit button--------------------*/}
          <SubmitButton data-qa="login-form-submit-button">
            <Translate i18nKey={"LoginPage.LoginForm.submitBtn"} />
          </SubmitButton>
        </form>
      </Form>
      {canShowDialogs && searchParams.get("accountBlocked") === "open" && (
        <LazyAccountBlockedModal />
      )}
      {canShowDialogs &&
        searchParams.get("foreignAccountIncomplete") === "open" &&
        searchParams.get("footerDialog") !== "open" && (
          <LazyForeignAccountIncompleteDialog />
        )}
      {canShowDialogs && searchParams.get("footerDialog") === "open" && (
        <LazyForeignAccountIncompleteDialogFooter />
      )}

      {canShowDialogs && searchParams.get("passwordExpiring") === "open" && (
        <LazyExpiringPasswordDialog />
      )}
      {canShowDialogs && searchParams.get(verifyContacts) === "open" && (
        <LazyPostLoginDialog />
      )}
      {canShowDialogs && searchParams.get("listModal") === "open" && (
        <LazyMultiModalDialog />
      )}
      {canShowDialogs && searchParams.get(MFA_DIALOG_PARAM) === "open" && (
        <LazyMfaVerificationDialog />
      )}
      {searchParams.get(recoveryPasswordParam) === "true" && (
        <LazyPasswordRecoveryDialog />
      )}

      <DevTool control={form.control} />
    </>
  )
}

function isEmail(value: string) {
  return z.email().safeParse(value).success
}

function isTrustedDevice(
  response: TSsoLoginResponse | TSsoLoginV3Response | TOAuthLoginV2Response,
) {
  if ("mfaEnabled" in response && response.mfaEnabled) {
    return !response.challengeRequired
  }
}

/**
 * Save device info in Zustand store and set deviceId cookie
 * @param deviceInfo The device information to save
 */
function saveDeviceInfo(deviceInfo: DeviceInfo) {
  const deviceInfoStore = useDeviceInfoStore.getState()

  const { deviceId, deviceName, appName, pushId } = deviceInfo

  deviceInfoStore.setDeviceName(deviceName)
  deviceInfoStore.setAppName(appName)
  if (pushId) {
    deviceInfoStore.setPushId(pushId)
  }
  if (deviceId) {
    Cookies.set(DEVICE_ID_COOKIE_NAME, deviceId)
  } else {
    Cookies.remove(DEVICE_ID_COOKIE_NAME)
  }
}
